import { type ReactNode } from 'react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetSection,
  ModalSheetHeaderShell,
  ModalSheetHeaderCloseButton,
} from '@/components/ModalSheet'
import { Button } from '@/components/Button'
import styles from './ManageItemsInBundleForm.module.css'
import { SwitchCheckbox } from '@/components/SwitchCheckbox'
import { Checkbox } from '@/components/Checkbox'
import { SearchField } from '@/components/SearchField'

export interface ManageItemsInBundleFormProps {
  showing: boolean
  onClose: () => unknown
  searchText: string
  onSearchTextChange: (text: string) => unknown
  totalSelected: number
  onSubmit: () => unknown
  /**
   * The infinite scroll loader, rendered below the items.
   */
  loader?: ReactNode
  children?: ReactNode
  testId?: string
}

export function ManageItemsInBundleForm({
  showing,
  onClose,
  searchText,
  onSearchTextChange,
  totalSelected,
  onSubmit,
  loader,
  children,
  testId,
}: ManageItemsInBundleFormProps) {
  return (
    <ModalSheet
      disableCloseOnOutsideClick
      showing={showing}
      onRequestClose={onClose}
    >
      <ModalSheetHeaderShell size="auto">
        <div className={styles.headerShell}>
          <div className={styles.header}>
            <div className={styles.title}>Add service Items</div>
            <div className={styles.closeButtonContainer}>
              <ModalSheetHeaderCloseButton />
            </div>
          </div>
          <div className={styles.searchContainer}>
            <SearchField
              autoFocus
              stretch
              className={styles.search}
              placeholder="Type to search service items"
              onChange={onSearchTextChange}
              value={searchText}
              showClearButton
              testId={testId && `${testId}-search-field`}
            />
          </div>
        </div>
      </ModalSheetHeaderShell>

      <ModalSheetContent>
        <ModalSheetSection spaced>
          <div className={styles.heading}>
            <div>Select all that apply</div>
            <div>Mark as base item</div>
          </div>
          <div>
            {children}
            {loader}
          </div>
        </ModalSheetSection>
      </ModalSheetContent>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            testId={testId && `${testId}-close-button`}
            onPress={onClose}
            variant="default"
          />

          <Button
            label={`Assign ${totalSelected}`}
            onPress={onSubmit}
            variant="primary"
            testId={testId && `${testId}-assign-button`}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

export interface IncludedItemSelectorProps {
  name: string
  description: string
  isIncludedItem: boolean
  onToggleIncludedItem: () => unknown
  isBaseItem: boolean
  onToggleBaseItem: () => unknown
  isBaseItemDisabled: boolean
  testId?: string
}

export function IncludedItemSelector({
  name,
  description,
  isIncludedItem,
  onToggleIncludedItem,
  isBaseItem,
  onToggleBaseItem,
  isBaseItemDisabled,
  testId,
}: IncludedItemSelectorProps) {
  return (
    <div className={styles.includedItem}>
      <div className={styles.selectorContainer}>
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control -- Checkbox renders a native input nested inside this label */}
        <label className={styles.label}>
          <Checkbox
            checked={isIncludedItem}
            onChange={onToggleIncludedItem}
            testId={testId && `${testId}-checkbox-${name}`}
          />
          <div className={styles.itemInfo}>
            <div className={styles.labelText}>{name}</div>
            <div className={styles.labelDescription}>{description}</div>
          </div>
        </label>
      </div>

      <div>
        <SwitchCheckbox
          noText
          isDisabled={isBaseItemDisabled}
          value={isBaseItem}
          onChange={onToggleBaseItem}
          testId={testId && `${testId}-switchcheckbox-${name}`}
        />
      </div>
    </div>
  )
}
